import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormStatusService } from '../status.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { FormDataService } from '../data.service';
import { FormEmailService } from '../email.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { formStatus, formTypes, FormStatus } from '../../../form/form.constants';
import { User } from '../../../user/models/user.model';
import { FormShareService } from '../share.service';
import { mergeMap } from 'rxjs/operators';
import {StatusLog} from '../../../project/models/status-log.model';
import {projectStatus} from '../../../project/project.constants';
import {ProjectDetailService} from '../../../project/services/project-shared.service';
import {ProjectDataService} from '../../../project/services/project-data.service';

@Injectable()
export class SubmitFormActionHandlerService extends FormActionHandlerService {
  constructor(
    private dataService: FormDataService,
    private emailService: FormEmailService,
    private statusService: FormStatusService,
    private shareService: FormShareService,
    private userContext: UserContextService,
    private swal: SwalService,
    private projectDetailService: ProjectDetailService,
    private projectService: ProjectDataService,
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    return new Promise((resolve, reject) => {
      that.swal.load('Submitting this form to LED for review...');
      that.statusService.changeStatus(that.getSubmitTargetStatus());
      var date = new Date(Date.now());
      this.shareService.form.modifiedDate = date;
      that.dataService
        .saveForm()
        .pipe(mergeMap(() => that.dataService.takeFormSnapShot()))
        .subscribe(
          successfulSave => {
            that.swal.load('Emailing stakeholders...');
            let ccList = new Array<User>();
            // get assessor for the current project parish.
            this.shareService.getFormAssessors().subscribe(cc => {
              // Filters the assessor who opted for the program which mathces current project incentive program.
              const assessorList = cc.filter(
                user =>
                  user.incentivePrograms.indexOf(
                    that.shareService.project.projectInfo.incentiveProgram
                  ) >= 0
              );
              // CC assessors only for the following form Types.
              if (
                (this.shareService.form.type === formTypes.advance.abbrev ||
                  this.shareService.form.type === formTypes.application.abbrev ||
                  this.shareService.form.type === formTypes.projectApplication.abbrev ||
                  this.shareService.form.type === formTypes.afc.abbrev ||
                  this.shareService.form.type === formTypes.pcr.abbrev) &&
                assessorList.length > 0
              ) {
                assessorList.forEach(assessor => {
                  ccList.push(assessor);
                });
              }
              // Add program managers to the CC List.
              this.shareService.getProgramManager().subscribe(manager => {
                ccList.push(manager);
                // Send Payment received email to the applicant and CC manager and assessors.

                // If the project is confidential, clear the ccList.
                // The whole implementation has to be redone with a bit more time
                // to allow a situation where we do not even go to get CCs or managers from the api.
                // This would make it more efficient,
                // but we do not have time to do this now.
                if (that.shareService.project.projectInfo.isConfidential) {
                  ccList = [];
                }

                that.emailService
                  .sendSubmissionSuccessfulEmail(ccList)
                  .subscribe(
                    successfulEmail => {
                      that.swal
                        .success({
                          title:
                            'Form has been successfully submitted to LED for review!',
                          text:
                            'An LED manager will be in contact with you soon.'
                        })
                        .then(() => {
                          if (this.shareService.form.type === formTypes.stepApplication.abbrev) {
                            // Construct status object
                            const newStatusLog = <StatusLog>{
                              statusDate: new Date(),
                              creator: that.userContext.currentUser.id
                            };
                            newStatusLog.status = projectStatus.projectInReview;

                            that.projectDetailService.project.projectStatusLog.unshift(newStatusLog);
                            that.swal.load(
                              'Updating project status...',
                              'This will take just a moment.'
                            );

                            that.projectService.updateProject(that.projectDetailService.project).subscribe(
                              () => {
                                that.swal.success({
                                  title: 'Success!',
                                  text: `Project status is now ${newStatusLog.status}.`
                                });

                                // todo we need to send an email saying project status change or we need to
                                //  modity the form status email to include the project status change

                                resolve();
                              },
                              error => {
                                resolve()
                              }
                            );
                          } else {
                            resolve();
                          }
                        })
                        .catch(resolve);
                    },
                    errorEmailing =>
                      reject(
                        'An error occurred while emailing stakeholders of this project, ' +
                          'but your form has still been successfully submitted and an LED manager will be in contact with you soon.'
                      )
                  );
              });
            });
          },
          errorSaving => {
            if (errorSaving === 409) {
              reject(errorSaving);
            } else {
              reject(
                'We were unable to submit your form to LED for review. Please try again later.'
              );
            }
          }
        );
    });
  }

  private getSubmitTargetStatus(): FormStatus {
    const form = this.shareService.form;
    const rdApplicationFormTypes = [
      formTypes.rdLqre.abbrev,
      formTypes.rdLqre100.abbrev,
      formTypes.rdIncreaseInRD.abbrev,
      formTypes.rdSbir.abbrev
    ];

    const statuses = form.statuses || [];
    const priorStatus = statuses.length > 1 ? statuses[1].status : null;

    if (
      rdApplicationFormTypes.indexOf(form.type) !== -1 &&
      priorStatus === formStatus.examinationSubmitted
    ) {
      return formStatus.examinationSubmitted;
    }

    return formStatus.received;
  }
}
